import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import api from '../api';
import {
  HiOutlineLightningBolt,
  HiOutlinePlay,
  HiOutlineCreditCard,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineClock,
  HiOutlineArrowRight,
  HiOutlineSparkles,
} from 'react-icons/hi';

function StatCard({ label, value, icon: Icon, sub }) {
  return (
    <div className="card p-5 flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
        <p className="text-2xl font-bold mt-1">{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
      </div>
      <Icon size={22} className="text-brand-500" />
    </div>
  );
}

export default function Dashboard() {
  const user = useSelector((s) => s.auth?.user);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get('/dashboard/stats')
      .then((res) => setStats(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const statusIcon = (status) => {
    if (status === 'success') return <HiOutlineCheckCircle className="text-green-500" size={18} />;
    if (status === 'error') return <HiOutlineXCircle className="text-red-500" size={18} />;
    return <HiOutlineClock className="text-yellow-500" size={18} />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-8 max-w-6xl">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">
            Welcome{user?.email ? `, ${user.email.split('@')[0]}` : ''}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Here's your automation overview
          </p>
        </div>

        <Link to="/scenarios" className="btn-primary flex items-center gap-2">
          <HiOutlineLightningBolt size={16} /> New Scenario
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          label="Scenarios"
          value={stats?.totalScenarios ?? 0}
          icon={HiOutlineLightningBolt}
        />
        <StatCard
          label="Executions"
          value={stats?.totalExecutions ?? 0}
          icon={HiOutlinePlay}
        />
        <StatCard
          label="Credits"
          value={(stats?.credits ?? 0).toLocaleString()}
          icon={HiOutlineCreditCard}
          sub={
            stats?.creditsResetDate
              ? `Resets ${new Date(stats.creditsResetDate).toLocaleDateString()}`
              : null
          }
        />
      </div>

      {/* Upgrade Banner */}
      {user?.plan === 'free' && (
        <div className="bg-gradient-to-r from-brand-600 to-purple-600 rounded-xl p-6 text-white">
          <div className="flex items-center gap-2 mb-1">
            <HiOutlineSparkles size={18} />
            <span className="font-semibold">Upgrade your plan</span>
          </div>
          <p className="text-sm text-white/80">
            Unlock more credits and unlimited scenarios.
          </p>
          <Link
            to="/billing"
            className="inline-block mt-3 px-4 py-2 bg-white text-brand-600 rounded-lg text-sm font-medium"
          >
            View Plans
          </Link>
        </div>
      )}

      {/* Activity */}
      <div>
        <div className="flex justify-between mb-4">
          <h2 className="text-lg font-semibold">Recent Activity</h2>
          <Link to="/executions" className="text-sm text-brand-600 flex items-center gap-1">
            View all <HiOutlineArrowRight size={14} />
          </Link>
        </div>

        {stats?.recentActivity?.length ? (
          <div className="card divide-y">
            {stats.recentActivity.map((item) => (
              <Link
                key={item.id}
                to={`/executions/${item.id}`}
                className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                {statusIcon(item.status)}

                <div className="flex-1">
                  <p className="text-sm font-medium">{item.scenarioName}</p>
                  <p className="text-xs text-gray-400">
                    {new Date(item.createdAt).toLocaleString()}
                  </p>
                </div>

                <span className="text-xs capitalize">{item.status}</span>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-400">
            <HiOutlinePlay size={28} className="mx-auto mb-2" />
            <p>No activity yet</p>
          </div>
        )}
      </div>
    </div>
  );
}

